// Analytics service for tracking user behavior and conversions
// Supports Google Analytics 4 and Google Tag Manager

declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
    dataLayer: any[];
  }
}

export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  customParameters?: Record<string, any>;
}

export interface UserProperties {
  user_id?: string;
  user_type?: string;
  organization_id?: string;
  plan_type?: string;
  signup_date?: string;
  onboarding_completed?: boolean;
}

class AnalyticsService {
  private initialized = false;
  private gtag: typeof window.gtag | null = null;
  private dataLayer: typeof window.dataLayer | null = null;

  constructor() {
    this.initialize();
  }

  private initialize() {
    if (typeof window === 'undefined') return;

    // Initialize gtag if available
    if (window.gtag) {
      this.gtag = window.gtag;
    }

    // Initialize dataLayer if available
    if (window.dataLayer) {
      this.dataLayer = window.dataLayer;
    }

    this.initialized = true;
  }

  // Set user properties for tracking
  setUserProperties(properties: UserProperties) {
    if (!this.initialized || !this.gtag) return;

    this.gtag('config', 'GA_MEASUREMENT_ID', {
      custom_map: {
        dimension1: 'user_type',
        dimension2: 'organization_id',
        dimension3: 'plan_type',
        dimension4: 'signup_date',
        dimension5: 'onboarding_completed',
      },
      user_properties: properties,
    });

    // Also send to GTM if available
    if (this.dataLayer) {
      this.dataLayer.push({
        event: 'user_properties_set',
        user_properties: properties,
      });
    }
  }

  // Track page views
  trackPageView(pagePath: string, pageTitle?: string) {
    if (!this.initialized || !this.gtag) return;

    this.gtag('config', 'GA_MEASUREMENT_ID', {
      page_path: pagePath,
      page_title: pageTitle,
    });

    // GTM page view
    if (this.dataLayer) {
      this.dataLayer.push({
        event: 'page_view',
        page_path: pagePath,
        page_title: pageTitle,
      });
    }
  }

  // Track custom events
  trackEvent(event: AnalyticsEvent) {
    if (!this.initialized || !this.gtag) return;

    this.gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
      ...event.customParameters,
    });

    // GTM custom event
    if (this.dataLayer) {
      this.dataLayer.push({
        event: event.action,
        event_category: event.category,
        event_label: event.label,
        value: event.value,
        ...event.customParameters,
      });
    }
  }

  // Predefined tracking methods for common events
  trackSignup(method: string = 'email') {
    this.trackEvent({
      action: 'sign_up',
      category: 'engagement',
      label: method,
    });
  }

  trackLogin(method: string = 'email') {
    this.trackEvent({
      action: 'login',
      category: 'engagement',
      label: method,
    });
  }

  trackPlanView(planId: string, source: string = 'pricing_page') {
    this.trackEvent({
      action: 'view_plan',
      category: 'ecommerce',
      label: planId,
      customParameters: {
        plan_id: planId,
        source: source,
      },
    });
  }

  trackPlanSelect(planId: string, billingCycle: string = 'monthly') {
    this.trackEvent({
      action: 'select_plan',
      category: 'ecommerce',
      label: planId,
      customParameters: {
        plan_id: planId,
        billing_cycle: billingCycle,
      },
    });
  }

  trackCheckoutStart(planId: string, value: number) {
    this.trackEvent({
      action: 'begin_checkout',
      category: 'ecommerce',
      label: planId,
      value: value,
      customParameters: {
        currency: 'USD',
        items: [{
          item_id: planId,
          item_name: planId,
          price: value,
          quantity: 1,
        }],
      },
    });
  }

  trackPurchase(planId: string, value: number, transactionId: string) {
    this.trackEvent({
      action: 'purchase',
      category: 'ecommerce',
      label: planId,
      value: value,
      customParameters: {
        currency: 'USD',
        transaction_id: transactionId,
        items: [{
          item_id: planId,
          item_name: planId,
          price: value,
          quantity: 1,
        }],
      },
    });
  }

  trackCampaignCreate(platform: string, budget: number) {
    this.trackEvent({
      action: 'create_campaign',
      category: 'engagement',
      label: platform,
      value: budget,
      customParameters: {
        platform: platform,
        budget: budget,
      },
    });
  }

  trackOnboardingStep(stepId: string, completed: boolean) {
    this.trackEvent({
      action: completed ? 'onboarding_step_complete' : 'onboarding_step_start',
      category: 'engagement',
      label: stepId,
      customParameters: {
        step_id: stepId,
        completed: completed,
      },
    });
  }

  trackOnboardingComplete() {
    this.trackEvent({
      action: 'onboarding_complete',
      category: 'engagement',
      customParameters: {
        completion_rate: 100,
      },
    });
  }

  trackFeatureUsage(feature: string, context?: string) {
    this.trackEvent({
      action: 'feature_use',
      category: 'engagement',
      label: feature,
      customParameters: {
        feature_name: feature,
        context: context,
      },
    });
  }

  trackError(errorType: string, errorMessage: string, context?: string) {
    this.trackEvent({
      action: 'exception',
      category: 'error',
      label: errorType,
      customParameters: {
        error_message: errorMessage,
        context: context,
      },
    });
  }

  // Track time spent on page
  trackTimeOnPage(pagePath: string, timeSpent: number) {
    this.trackEvent({
      action: 'time_on_page',
      category: 'engagement',
      label: pagePath,
      value: timeSpent,
      customParameters: {
        time_spent_seconds: timeSpent,
      },
    });
  }

  // Track scroll depth
  trackScrollDepth(pagePath: string, scrollDepth: number) {
    this.trackEvent({
      action: 'scroll_depth',
      category: 'engagement',
      label: pagePath,
      value: scrollDepth,
      customParameters: {
        scroll_percentage: scrollDepth,
      },
    });
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// React hook for using analytics
export const useAnalytics = () => {
  return analytics;
};